import { observable, action, computed } from 'mobx';

import { analytics } from 'core/analytics';
import { Model, App, ArrayApp, ModelRegistry, AppRegistry } from 'core';

import { clientStore } from 'apps/client/app';

class RequestError extends Error {
  constructor(response) {
    super(response.statusText);
    this.name = 'RequestError';
    this.code = response.status;
  }
}

export const readBlobAs = (blob, type) => {
  const readerName = `readAs${type}`;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = err => {
      reader.abort();
      reject(err);
    };
    reader[readerName](blob);
  });
};

export const readAs = (app, type) => {
  const fieldName = `fileAs${type}`;
  return app
    .readFile()
    .then(blob => readBlobAs(blob, type))
    .then(result => {
      app[fieldName] = result;
    })
    .catch(err => {
      if (err.name === 'RequestError') {
        // Supress these errors since they might just be because the file
        // doesn't exist
        return;
      }
      const scopingFunction = scope => {
        scope.setExtra('permalink', app.permalink);
        scope.setExtra('fieldName', fieldName);
      };
      analytics.logError(err, scopingFunction);
    })
    .finally(() => {
      app.readingFile = false;
    });
};

export const readFile = app => {
  if (!(app && app.permalink)) {
    return Promise.reject(new Error('No download Path'));
  }

  app.readingFile = true;
  return fetch(app.permalink, { credentials: 'include' })
    .then(response => {
      if (!response.ok) {
        throw new RequestError(response);
      }
      return response.blob();
    })
    .then(blob => app.setBlob(blob))
    .catch(err => {
      app.error = err;
      throw app.error;
    });
};

export class FileConversionModel extends Model {
  static registryName = 'FileConversionModel';
  static ModelProps = {
    convertedFile: 'FileModel',
  };

  @observable id;
  @observable viewerSemver;
  @observable status;
  @observable reason;
  @observable errorCode;
  @observable convertedFile;
}
ModelRegistry.add(FileConversionModel);

export class FileModel extends Model {
  static registryName = 'FileModel';
  static ModelProps = {
    conversionGltfjson: 'FileConversionModel',
    originalFile: 'FileModel',
    creator: 'UserModel',
  };

  @observable id;
  @observable filename;
  @observable mimeType;
  @observable path;
  @observable url;
  @observable uploadUrl;
  @observable version = 0;
  @observable hmac;
  @observable private;
  @observable permalink;
  @observable isCopy;
  @observable space = null;
  @observable size;
  @observable gitHash;
  @observable fileLastModified;
  @observable completed;
  @observable cancelled;
  @observable conversionGltfjson = null;
  @observable originalFile;
  @observable creator = null;
  @observable canDownload;
  @computed get sizeStr() {
    if (!this.size) {
      return '0 kB';
    }
    const kbytes = this.size / 1000;
    if (kbytes < 1000) {
      return `${Math.round(kbytes).toString()} kB`;
    }
    const mbytes = Math.floor(kbytes / 1000);
    const kbytes_remainder = Math.round((kbytes % 1000) / 100);
    if (mbytes < 1000) {
      return `${mbytes.toString()}.${kbytes_remainder.toString()} MB`;
    }
    const gbytes = Math.floor(mbytes / 1000);
    const mbytes_remainder = Math.round((mbytes % 1000) / 100);
    return `${gbytes.toString()}.${mbytes_remainder.toString()} GB`;
  }

  @computed get viewerPath() {
    return clientStore.routerStore.router.buildUrl('viewer', {
      fileID: this.id,
      filename: this.filename,
    });
  }
}
ModelRegistry.add(FileModel);

export class FileApp extends App {
  static registryName = 'FileApp';
  static Model = FileModel;
  @observable.ref localFile = null;
  @observable readingFile = false;
  @observable fileAsText = null;
  @observable fileAsArrayBuffer = null;
  @observable fileAsDataURL = null;
  @observable error = null;

  @observable isBeingRemoved = false;

  @action SetIsBeingRemoved = bool => {
    this.isBeingRemoved = bool;
  };

  @computed get src() {
    return this.completed || this.completed === undefined
      ? this.url
      : this.localFile
      ? URL.createObjectURL(this.localFile)
      : '';
  }

  @action setBlob = blob => {
    this.blob = blob;
    return blob;
  };

  @action readFile = () => readFile(this);
  @action readAsText = () => readAs(this, 'Text');
  @action readAsArrayBuffer = () => readAs(this, 'ArrayBuffer');
  @action readAsDataURL = () => readAs(this, 'DataURL');
  @action SetLocalFile = fileObj => {
    this.private = fileObj.private;
    this.filename = fileObj.name;
    this.mimeType = fileObj.type;
    this.size = fileObj.size;
    this.fileLastModified = fileObj.lastModifiedDate;
    this.completed = false;
    this.localFile = fileObj;
  };

  @action SetLocalFileRef = fileObj => {
    this.localFile = fileObj;
  };
}
AppRegistry.add(FileApp);

export class FilesApp extends ArrayApp {
  static registryName = 'FilesApp';
  static ElementApp = FileApp;
}
AppRegistry.add(FilesApp);
